import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { useTheme } from '../lib/theme';
import { auditAPI, billingAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Shield, Upload, FileText, Trash2, Download, Eye,
  BarChart3, AlertTriangle, Clock, CreditCard, Sun, Moon, Globe
} from 'lucide-react';

export default function Dashboard() {
  const { user, logout, refreshUser } = useAuth();
  const { t, language, changeLanguage } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pollMsg, setPollMsg] = useState('');

  const fetchAudits = useCallback(async () => {
    try {
      await refreshUser(); // Fresh user = lifetime stats (decoupled from audit docs)
      const res = await auditAPI.history();
      setAudits(res.data.audits || []);
    } catch { /* ignore */ }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll payment status (ONE TIME ONLY to prevent infinite loop)
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;

    // Mark as processed to prevent re-execution
    let processed = false;

    const verifyPayment = async () => {
      if (processed) return;
      processed = true;

      setPollMsg('Verifying payment...');
      let attempts = 0;
      const maxAttempts = 6;
      const pollInterval = 2500;

      const poll = async () => {
        if (attempts >= maxAttempts) {
          setPollMsg('');
          // Clean URL after verification attempts
          window.history.replaceState({}, document.title, window.location.pathname);
          return;
        }
        attempts++;
        try {
          const res = await billingAPI.status(sessionId);
          if (res.data.payment_status === 'paid') {
            setPollMsg('Payment successful! Your plan is now active.');
            await refreshUser();
            await fetchAudits(); // Refresh audits to show unlocked reports
            setTimeout(() => setPollMsg(''), 4000);
            // Clean URL after successful payment
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
          }
          if (res.data.status === 'expired') {
            setPollMsg('Payment session expired.');
            setTimeout(() => setPollMsg(''), 4000);
            // Clean URL
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
          }
        } catch { /* ignore */ }
        setTimeout(poll, pollInterval);
      };

      poll();
    };

    verifyPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run ONCE on mount, ignore searchParams/refreshUser changes

  useEffect(() => { fetchAudits(); }, [fetchAudits]);

  // If user landed on Dashboard with intended_plan (e.g. logged in after saving plan), initiate checkout
  useEffect(() => {
    if (!user) return;
    const intendedPlanRaw = localStorage.getItem('intended_plan');
    if (!intendedPlanRaw) return;
    let planId = 'auditor';
    let interval = 'year';
    try {
      const parsed = JSON.parse(intendedPlanRaw);
      if (parsed && typeof parsed.plan === 'string') {
        planId = parsed.plan;
        interval = (parsed.interval === 'month' || parsed.interval === 'year') ? parsed.interval : 'year';
      }
    } catch {
      if (typeof intendedPlanRaw === 'string' && (intendedPlanRaw === 'auditor' || intendedPlanRaw === 'firm')) {
        planId = intendedPlanRaw;
      }
    }
    localStorage.removeItem('intended_plan');
    const currency = language === 'fr' ? 'eur' : 'usd';
    billingAPI.checkout(window.location.origin, planId, currency, language, interval)
      .then((res) => { if (res.data?.url) window.location.href = res.data.url; })
      .catch(() => { /* ignore */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleDelete = async (auditId) => {
    try {
      await auditAPI.delete(auditId);
      setAudits(prev => prev.filter(a => a.audit_id !== auditId));
    } catch { /* ignore */ }
  };

  const handleDownloadPdf = async (auditId, filename) => {
    try {
      const res = await auditAPI.downloadPdf(auditId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SheetArmor_${filename}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  const handleUpgrade = (planId = 'auditor') => {
    // Send the user to the pricing section so they can choose between Auditor and Firm
    window.location.href = '/#pricing';
  };

  const handleManageBilling = async () => {
    try {
      const res = await billingAPI.portal();
      if (res.data.url) window.location.href = res.data.url;
    } catch (error) {
      // Handle error gracefully when user has no active subscription
      const errorMsg = error?.response?.status === 400
        ? language === 'fr'
          ? 'Vous n\'avez pas d\'abonnement actif à gérer pour le moment. Veuillez compléter votre paiement.'
          : 'You don\'t have an active subscription to manage yet. Please complete your payment first.'
        : language === 'fr'
          ? 'Impossible d\'accéder au portail de facturation. Veuillez réessayer.'
          : 'Unable to access billing portal. Please try again.';

      alert(errorMsg);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 50) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getTimeLeft = (expiresAt) => {
    const diff = new Date(expiresAt) - new Date();
    if (diff <= 0) return 'Expired';
    const h = Math.floor(diff / 3600000);
    return `${h}h`;
  };

  // Stats from User (persistent) — not from audits (deleted after 24h)
  const stats = {
    total: user?.lifetime_total_audits ?? 0,
    avgScore: user?.lifetime_avg_score ?? 0,
    totalIssues: user?.lifetime_total_issues ?? 0,
  };

  const planLabel = user?.plan === 'firm' ? t('dash.firm') :
    user?.plan === 'auditor' ? t('dash.auditor') : t('dash.free');

  return (
    <div className="min-h-screen bg-white dark:bg-[#050505] text-slate-900 dark:text-white transition-colors" data-testid="dashboard-page">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-white/5 bg-white/90 dark:bg-[#050505]/90 backdrop-blur-lg sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <Shield className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
              <span className="font-heading text-lg tracking-tight">SheetArmor</span>
            </Link>
            <Separator orientation="vertical" className="h-5 bg-slate-300 dark:bg-white/10 mx-2" />
            <span className="text-slate-600 dark:text-neutral-500 text-sm font-body">{t('dash.title')}</span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => changeLanguage(language === 'en' ? 'fr' : 'en')}
              className="text-slate-600 hover:text-slate-900 dark:text-neutral-400 dark:hover:text-white h-8 px-2"
              title={language === 'en' ? 'Switch to French' : 'Basculer en anglais'}
            >
              <Globe className="w-4 h-4 mr-1" strokeWidth={1.5} />
              <span className="text-xs font-body">{language === 'en' ? 'FR' : 'EN'}</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="text-slate-600 hover:text-slate-900 dark:text-neutral-400 dark:hover:text-white h-8 w-8 p-0"
              title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
            >
              {theme === 'dark' ? (
                <Sun className="w-4 h-4" strokeWidth={1.5} />
              ) : (
                <Moon className="w-4 h-4" strokeWidth={1.5} />
              )}
            </Button>
            <Separator orientation="vertical" className="h-5 bg-slate-300 dark:bg-white/10" />
            {/* Admin shortcut — God Mode: only visible to hello@sheetarmor.io */}
            {user?.email?.toLowerCase() === 'hello@sheetarmor.io' && (
              <Link to="/admin">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-blue-600 hover:text-blue-500 hover:bg-blue-600/10 h-8 px-2 gap-1.5"
                  title="Admin Dashboard (God Mode)"
                  data-testid="admin-shortcut-button"
                >
                  <Shield className="w-3.5 h-3.5" strokeWidth={2} />
                  <span className="text-xs font-body">Admin</span>
                </Button>
              </Link>
            )}
            <Link to="/settings">
              <Button variant="ghost" className="text-slate-600 hover:text-slate-900 dark:text-neutral-400 dark:hover:text-white text-xs h-8 font-body" data-testid="settings-button">
                {user?.name || user?.email}
              </Button>
            </Link>
            <Button variant="ghost" onClick={logout} className="text-slate-500 hover:text-slate-900 dark:text-neutral-500 dark:hover:text-white text-xs h-8 font-body" data-testid="logout-button">
              {t('settings.logout')}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Payment message */}
        {pollMsg && (
          <div className="mb-4 p-3 bg-blue-600/10 border border-blue-600/20 rounded-sm text-blue-400 text-sm font-body" data-testid="payment-message">
            {pollMsg}
          </div>
        )}

        {/* Plan + Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-slate-200 dark:bg-white/10 rounded-sm overflow-hidden mb-6">
          <div className="bg-slate-50 dark:bg-[#0A0A0A] p-5">
            <p className="text-slate-600 dark:text-neutral-500 text-xs font-body mb-1 uppercase tracking-wider">{t('dash.plan')}</p>
            <div className="flex items-center gap-2 mb-2">
              <Badge variant={user?.plan !== 'free' ? 'default' : 'secondary'}
                className={user?.plan === 'firm' ? 'bg-blue-600 text-white' : user?.plan === 'auditor' ? 'bg-blue-600/70 text-white' : 'bg-neutral-800 text-neutral-300'}>
                {planLabel}
              </Badge>
            </div>
            {user?.plan === 'auditor' && (
              <div className="mt-2">
                <p className="text-slate-600 dark:text-neutral-400 text-xs font-body mb-1">{t('dash.audits_used').replace('{count}', user?.audits_used || 0)}</p>
                <div className="w-full bg-slate-200 dark:bg-neutral-800 rounded-full h-1.5">
                  <div
                    className="bg-blue-600 h-1.5 rounded-full transition-all"
                    style={{ width: `${Math.min((user?.audits_used || 0) / 10 * 100, 100)}%` }}
                  />
                </div>
                <p className="text-slate-500 dark:text-neutral-500 text-[10px] mt-1 font-body">
                  {t('dash.credits_reset').replace('{date}', (() => {
                    if (!user?.quota_resets_at) return language === 'fr' ? 'Synchronisation en cours...' : 'Syncing...';
                    try {
                      const d = new Date(user.quota_resets_at);
                      return isNaN(d.getTime()) ? (language === 'fr' ? 'Synchronisation en cours...' : 'Syncing...') : d.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
                    } catch { return language === 'fr' ? 'Synchronisation en cours...' : 'Syncing...'; }
                  })())}
                </p>
              </div>
            )}
            {user?.plan === 'firm' && (
              <p className="text-slate-600 dark:text-neutral-400 text-xs font-body mt-2">{t('dash.audits_unlimited')}</p>
            )}
            {user?.plan === 'free' && (
              <Button onClick={handleUpgrade} size="sm"
                className="mt-3 bg-blue-600 hover:bg-blue-700 text-white text-xs font-body tracking-widest rounded-sm h-8"
                data-testid="upgrade-button">
                <CreditCard className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                {t('dash.upgrade')}
              </Button>
            )}
            {(user?.plan === 'auditor' || user?.plan === 'firm') && (
              <Button onClick={handleManageBilling} size="sm"
                className="mt-3 bg-slate-200 hover:bg-slate-300 text-slate-900 dark:bg-neutral-800 dark:hover:bg-neutral-700 dark:text-white text-xs font-body tracking-wide rounded-sm h-8 w-full"
                data-testid="manage-billing-button">
                <CreditCard className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                {t('dash.manage_billing')}
              </Button>
            )}
          </div>
          <div className="bg-slate-50 dark:bg-[#0A0A0A] p-5">
            <p className="text-slate-600 dark:text-neutral-500 text-xs font-body mb-1 uppercase tracking-wider">{t('dash.total_audits')}</p>
            <p className="font-heading text-3xl text-slate-900 dark:text-white">{stats.total}</p>
          </div>
          <div className="bg-slate-50 dark:bg-[#0A0A0A] p-5">
            <p className="text-slate-600 dark:text-neutral-500 text-xs font-body mb-1 uppercase tracking-wider">{t('dash.avg_score')}</p>
            <p className={`font-heading text-3xl ${getScoreColor(stats.avgScore)}`}>{stats.avgScore}</p>
          </div>
          <div className="bg-slate-50 dark:bg-[#0A0A0A] p-5">
            <p className="text-slate-600 dark:text-neutral-500 text-xs font-body mb-1 uppercase tracking-wider">{t('dash.total_issues')}</p>
            <p className="font-heading text-3xl text-slate-900 dark:text-white">{stats.totalIssues}</p>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl text-slate-900 dark:text-white">{t('dash.history')}</h2>
          <Link to="/upload">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-body tracking-widest h-9 rounded-sm" data-testid="new-audit-button">
              <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {t('dash.upload')}
            </Button>
          </Link>
        </div>

        {/* Audits Table */}
        {loading ? (
          <div className="text-center py-16">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : audits.length === 0 ? (
          <div className="border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-[#0A0A0A] rounded-sm p-12 text-center" data-testid="no-audits">
            <FileText className="w-10 h-10 text-slate-400 dark:text-neutral-700 mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-slate-600 dark:text-neutral-500 font-body text-sm mb-4">{t('dash.no_audits')}</p>
            <Link to="/upload">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-body tracking-widest h-9 rounded-sm" data-testid="first-audit-button">
                <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
                {t('dash.upload')}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-[#0A0A0A] rounded-sm overflow-hidden" data-testid="audits-table">
            <div className="grid grid-cols-12 gap-2 p-3 border-b border-slate-200 dark:border-white/5 text-slate-600 dark:text-neutral-500 text-xs font-body uppercase tracking-wider">
              <div className="col-span-4">File</div>
              <div className="col-span-1">{t('dash.score')}</div>
              <div className="col-span-2">{t('dash.issues')}</div>
              <div className="col-span-2">{t('dash.date')}</div>
              <div className="col-span-1">{t('dash.expires')}</div>
              <div className="col-span-2">{t('dash.actions')}</div>
            </div>
            {audits.map((audit) => (
              <div key={audit.audit_id}
                className="grid grid-cols-12 gap-2 p-3 border-b border-slate-200 dark:border-white/5 hover:bg-slate-100 dark:hover:bg-white/[0.02] items-center transition-colors"
                data-testid={`audit-row-${audit.audit_id}`}>
                <div className="col-span-4 flex items-center gap-2 min-w-0">
                  <FileText className="w-4 h-4 text-slate-500 dark:text-neutral-600 flex-shrink-0" strokeWidth={1.5} />
                  <span className="font-body text-sm text-slate-900 dark:text-white truncate">{audit.filename}</span>
                </div>
                <div className="col-span-1">
                  <span className={`font-mono text-sm font-bold ${getScoreColor(audit.health_score)}`}>
                    {audit.health_score}
                  </span>
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <span className="font-mono text-sm text-slate-900 dark:text-white">{audit.total_issues}</span>
                  {(audit.critical_count ?? audit.critical_issues) > 0 && (
                    <Badge className="bg-red-600/20 text-red-400 text-xs">{audit.critical_count ?? audit.critical_issues} {t('dash.critical').toLowerCase()}</Badge>
                  )}
                </div>
                <div className="col-span-2 text-slate-600 dark:text-neutral-500 text-xs font-body">
                  {new Date(audit.created_at).toLocaleDateString()}
                </div>
                <div className="col-span-1 flex items-center gap-1 text-slate-600 dark:text-neutral-500 text-xs font-body">
                  <Clock className="w-3 h-3" strokeWidth={1.5} />
                  {getTimeLeft(audit.expires_at)}
                </div>
                <div className="col-span-2 flex items-center gap-1">
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-600 hover:text-slate-900 dark:text-neutral-400 dark:hover:text-white"
                    onClick={() => navigate(`/report/${audit.audit_id}`)}
                    title={language === 'fr' ? 'Voir le rapport' : 'View report'}
                    data-testid={`view-audit-${audit.audit_id}`}>
                    <Eye className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-600 hover:text-blue-600 dark:text-neutral-400 dark:hover:text-blue-400"
                    onClick={() => handleDownloadPdf(audit.audit_id, audit.filename)}
                    title={language === 'fr' ? 'Télécharger le PDF' : 'Download PDF'}
                    data-testid={`download-pdf-${audit.audit_id}`}>
                    <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </Button>
                  {user?.plan === 'firm' ? (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-600 hover:text-red-600 dark:text-neutral-400 dark:hover:text-red-400"
                      onClick={() => handleDelete(audit.audit_id)}
                      title={language === 'fr' ? 'Supprimer l\'audit (Kill-Switch)' : 'Delete audit (Kill-Switch)'}
                      data-testid={`delete-audit-${audit.audit_id}`}>
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                  ) : (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-neutral-600 dark:text-neutral-700 cursor-not-allowed opacity-40"
                      disabled
                      title={language === 'fr' ? 'Suppression (Réservé au Plan Cabinet)' : 'Delete (Firm Plan only)'}
                      data-testid={`delete-audit-disabled-${audit.audit_id}`}>
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
